/**
 * Helpers extracted from ViewProfile to reduce cognitive complexity.
 */

import { mapApiResponseToFormValues, toPostgresArrayLiteral } from '../utils/userMapper';
import { ROLES, type UserRoleType, isRadiologist } from '../utils/roleUtils';
import type { User } from '../types/user.types';

export interface ProfileApiResponse {
  success: boolean;
  message?: string;
  data?: {
    id?: number;
    username?: string;
    email?: string;
    name?: string;
    tenantID?: string;
    roles?: string[];
    modality?: string[];
    dbUser?: {
      id?: number;
      username?: string;
      email?: string;
      full_name?: string;
      role?: string;
      status?: string;
      designation?: string;
    };
  };
  fromDatabase?: User;
}

type MappedFormValues = ReturnType<typeof mapApiResponseToFormValues>;

function resolveRadiologistModalities(
  apiResponse: ProfileApiResponse,
  mapped: MappedFormValues
): string[] {
  const dataModality = apiResponse.data?.modality;
  if (dataModality && Array.isArray(dataModality) && dataModality.length > 0) {
    return dataModality;
  }
  if (
    apiResponse.fromDatabase?.reporting_modality_access &&
    Array.isArray(apiResponse.fromDatabase.reporting_modality_access) &&
    apiResponse.fromDatabase.reporting_modality_access.length > 0
  ) {
    return apiResponse.fromDatabase.reporting_modality_access;
  }
  if (mapped.reportingModalityAccess && Array.isArray(mapped.reportingModalityAccess)) {
    return mapped.reportingModalityAccess;
  }
  return mapped.reportingModalityAccess || [];
}

export function buildInitialValuesForRole(
  role: UserRoleType,
  apiResponse: ProfileApiResponse,
  mapped: MappedFormValues
): Record<string, unknown> {
  switch (role) {
    case ROLES.USE_SUPER_ADMIN:
      return {
        fullName: mapped.fullName,
        gender: mapped.gender,
        dateOfBirth: mapped.dateOfBirth,
        username: mapped.username,
        email: mapped.email,
        phoneNo: mapped.phoneNo,
        employeeId: mapped.employeeId || '',
      };
    case ROLES.USE_ADMIN: {
      const adminDesignation =
        apiResponse.data?.dbUser?.designation || apiResponse.fromDatabase?.designation || '';
      return {
        employeeId: mapped.employeeId || '',
        fullName: mapped.fullName,
        phoneNo: mapped.phoneNo,
        email: mapped.email,
        gender: mapped.gender,
        username: mapped.username,
        designation: adminDesignation,
      };
    }
    case ROLES.USE_TECHNICIAN:
      return {
        ...mapped,
        shiftStart: mapped.shiftStart || '',
        shiftEnd: mapped.shiftEnd || '',
        experienceYears: mapped.experienceYears,
      };
    case ROLES.USE_RADIOLOGIST: {
      const reportingModalityAccess = resolveRadiologistModalities(apiResponse, mapped);
      return {
        ...mapped,
        modality: mapped.role === 'Technician' ? mapped.department : [],
        shiftStart: mapped.shiftStart || '',
        shiftEnd: mapped.shiftEnd || '',
        registrationNo: mapped.registrationNumber || '',
        specialty: mapped.specialty || '',
        designation: mapped.designation || '',
        reportingModalityAccess,
        signatureFileName: mapped.signatureFileName || '',
        signature: null,
        availabilityHours: mapped.availabilityHours || [],
        availabilityDays: mapped.availabilityDays || [],
      };
    }
    default:
      return { ...mapped };
  }
}

export function buildProfileJsonUpdateData(
  values: Record<string, unknown>,
  userRole: UserRoleType
): Record<string, unknown> {
  const phoneRaw = typeof values.phoneNo === 'string' ? values.phoneNo : '';
  const phoneNumber = phoneRaw.replaceAll(/\D/g, '').slice(-10);
  const updateData: Record<string, unknown> = {
    full_name: values.fullName,
    gender: values.gender,
    email: values.email,
    mobile_number: phoneNumber,
    username: values.username,
  };

  if (userRole === ROLES.USE_SUPER_ADMIN) {
    updateData.date_of_birth = values.dateOfBirth;
    return updateData;
  }
  if (userRole === ROLES.USE_ADMIN) {
    updateData.employee_id = values.employeeId;
    updateData.designation = values.designation;
    return updateData;
  }
  if (userRole === ROLES.USE_TECHNICIAN) {
    updateData.employee_id = values.employeeId;
    updateData.date_of_birth = values.dateOfBirth;
    updateData.shift_start_at = values.shiftStart;
    updateData.shift_end_at = values.shiftEnd;
    updateData.experience_years = values.experienceYears;
    return updateData;
  }
  if (isRadiologist(userRole)) {
    updateData.date_of_birth = values.dateOfBirth;
    updateData.registration_number = values.registrationNo;
    updateData.qualification = values.qualification;
    updateData.experience_years = values.experienceYears;
    const specialtyRaw = typeof values.specialty === 'string' ? values.specialty : '';
    const specialtyParts = specialtyRaw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    if (specialtyParts.length) {
      updateData.specialty = toPostgresArrayLiteral(specialtyParts);
    }
    updateData.designation = values.designation;
    updateData.reporting_modality_access = Array.isArray(values.reportingModalityAccess)
      ? values.reportingModalityAccess
      : [values.reportingModalityAccess];
    updateData.availability_hours = values.availabilityHours || [];
    updateData.availability_days = values.availabilityDays || [];
  }
  return updateData;
}

export function getProfileFieldError(
  errors: Record<string, unknown>,
  touched: Record<string, unknown>,
  values: Record<string, unknown>,
  fieldName: string
): string | undefined {
  const error = errors?.[fieldName];
  const isTouched = touched?.[fieldName];
  const fieldValue = values?.[fieldName];
  const hasValue = Array.isArray(fieldValue)
    ? fieldValue.length > 0
    : fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
  if (!(isTouched || hasValue) || !error) {
    return undefined;
  }
  return typeof error === 'string' ? error : undefined;
}

export type NotifyFn = (opts: {
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}) => void;

export function notifyProfileResult(
  uiNotificationService: { show: NotifyFn } | undefined,
  success: boolean,
  successMessage: string,
  errorMessage: string
): void {
  if (!uiNotificationService) {
    return;
  }
  if (success) {
    uiNotificationService.show({
      title: 'Success',
      message: successMessage,
      type: 'success',
      duration: 3000,
    });
    return;
  }
  uiNotificationService.show({
    title: 'Error',
    message: errorMessage,
    type: 'error',
    duration: 5000,
  });
}
